<script setup lang="ts">
import CoreIcon from '@core/core-icon.vue';
import {computed, PropType} from 'vue';
import {useI18n} from 'vue-i18n';
import {Bill, BillStatus, Currency} from "@store/api/interfaces";

const { t } = useI18n();
const props = defineProps({
	bill: {
		type: Object as PropType<Bill>,
		required: true
	}
});

const statusIcon = computed(() => {
	const icons: Partial<Record<BillStatus, { name: string; viewBox: string }>> = {
		created: {
			name: "clock",
			viewBox: "0 0 18 18"
		},
		// error: {
		// 	name: "payment_error",
		// 	viewBox: ""
		// },
		paid: {
			name: "checked",
			viewBox: "0 0 18 18"
		},
		refund: {
			name: "rotateRight",
			viewBox: "0 0 19 19"
		}
	};
	return props.bill?.status ? icons[props.bill.status] : null;
});

const currencyIcon = computed(() => {
	const icons: Partial<Record<Currency, { name: string; viewBox: string }>> = {
		xtr: { name: "star-stroke", viewBox: "0 0 18 17" },
		ton: { name: "star-stroke", viewBox: "0 0 18 17" }
	};

	return props.bill?.cost.currency ? icons[props.bill.cost.currency] ?? null : null;
});

const currencySymbol = computed(() => {
	const symbols: Partial<Record<Currency, String>> = {
		rub: "₽",
		usd: "$",
		usdt: "$"
	};
	return props.bill?.cost.currency ? symbols[props.bill.cost.currency] : null;
});

/**
 * Подпись счёта.
 *
 * У оплаченного пакета резервного трафика подписки нет (`subscription_months: 0`),
 * и «Оплата подписки» для него — неверная подпись: показываем сам продукт, а
 * оплаченность несут галочка и зелёный фон строки. Невыставленный и возвращённый
 * счёт продолжают показывать статус: иначе неоплаченный пакет читался бы как
 * купленный. У подписки с резервом к статусу дописывается месячная порция ГБ.
 */
const currentType = computed(() => {
	const gb = props.bill?.whitelist_gb;
	const status = t(`common.payment.status_${props?.bill?.status}`);

	if (props.bill?.type === 'whitelist_topup') {
		return props.bill?.status === 'paid'
			? t('common.payment.reserve_pack', { n: gb ?? 0 })
			: status;
	}

	return gb
		? `${status} ${t('common.payment.reserve_suffix', { n: gb })}`
		: status;
});

const currentProvider = computed(() => {
	return t(`common.payment.provider_${props?.bill?.pay_system}`)
});

const currentCost = computed(() => {
	const amount = props.bill?.cost.amount;
	return !amount ? amount || 0 : `-${amount}`;
});

const currentDate = computed<Date>(() => {
	return props.bill?.refund_at || props.bill?.paid_at || props.bill?.created_at || new Date();
});

/*const currentError = computed(() => {
	const { error } = props.fields;
	const translations = {
		1: t('common.payment.error_not_enough_money'),
	};
	return translations?.[error];
});*/

</script>

<template>
	<div
		class="item-history-payment"
		:class="`${bill?.status ? `item-history-payment_${bill.status}` : ''}`"
	>
		<div
			v-if="statusIcon"
			:class="['item-history-payment__icon', `item-history-payment__icon_${bill?.status}`]"
		>
			<core-icon v-bind="statusIcon" />
		</div>
		<div class="item-history-payment__content">
			<div
				v-if="currentType"
				class="item-history-payment__content-action font-16-pro-regular"
			>
				{{ currentType }}
			</div>
			<!-- currentError || currentProvider -->
			<div
				v-if="currentProvider"
				class="item-history-payment__content-method"
			>
				{{ currentProvider }}
			</div>
		</div>
		<div class="item-history-payment__pay">
			<div
				class="item-history-payment__pay-cost font-16-pro-regular"
			>
				<span>{{ currentCost }}</span>
				<core-icon
					v-if="currencyIcon"
					v-bind="currencyIcon"
				/>
				<span v-else-if="currencySymbol">{{ currencySymbol }}</span>
			</div>
			<div
				v-if="bill?.refund_at || bill?.paid_at || bill?.created_at"
				class="item-history-payment__pay-date"
			>
				{{ $d(currentDate, 'defaultFormat') }}
			</div>
		</div>
	</div>
</template>

<style lang="scss">
	// Строка как карточки кабинета: фон панели, тонкая рамка, круг 3,2 rem под
	// иконку. Статус несёт цвет иконки, а не заливка всей строки.
	.item-history-payment {
		display: flex;
		align-items: center;
		gap: 1.2rem;
		min-height: 5.2rem;
		padding: 0.6rem 1.2rem;
		border-radius: 1.6rem;
		background-color: var(--color-panel);
		border: 1px solid rgba(var(--color-active-rgb), 0.12);

		@include min-desktop {
			min-height: 6.2rem;
			padding: 0.8rem 1.6rem;
			gap: 1.6rem;
		}

		&__icon {
			flex: 0 0 auto;
			width: 3.2rem;
			height: 3.2rem;
			border-radius: 50%;
			display: flex;
			align-items: center;
			justify-content: center;
			background-color: var(--color-white);
			fill: var(--color-active);

			@include theme-dark {
				background-color: rgba(var(--color-active-rgb), 0.06);
			}

			&_error {
				fill: var(--color-orange);
			}

			&_paid {
				fill: var(--color-green);
			}

			svg {
				width: 1.6rem;
			}
		}

		&__content {
			flex: 1 1 auto;
			min-width: 0;
			display: flex;
			flex-direction: column;
			gap: 0.2rem;
		}
		&__content-action {
			// 1.2, а не 1: подпись счёта с резервом длиннее и на узком экране
			// переносится на вторую строку — со сплошным line-height они слипаются.
			line-height: 1.2;
		}
		&__content-method {
			@include font-12-pro-regular;
			opacity: 0.6;
			@include text-overflow;
		}

		&__pay {
			flex: 0 0 auto;
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			text-align: right;
			gap: 0.2rem;
		}
		&__pay-cost {
			display: flex;
			justify-content: flex-end;
			align-items: center;
			font-weight: 500;
			fill: transparent;
			stroke: currentColor;
			stroke-width: 1.5;
			svg {
				margin-top: -2px;
				margin-left: 0.6rem;
				width: 1.6rem;
			}
		}
		&__pay-date {
			@include font-12-pro-regular;
			opacity: 0.6;
		}
	}
</style>
